import React, { Suspense, lazy } from 'react';
import { Router, SwitchCamera, ShieldCheck, Server, Cloud, Wifi, Monitor, Laptop, Tablet, Smartphone, Printer, Box } from 'lucide-react';

const AWS_ICONS = {
  ROUTER: lazy(() => import('@thesvg/react/aws-res-amazon-vpc-router')),
  SWITCH: lazy(() => import('@thesvg/react/aws-res-amazon-vpc-elastic-network-interface')),
  FIREWALL: lazy(() => import('@thesvg/react/aws-aws-waf')),
  SERVER: lazy(() => import('@thesvg/react/aws-group-ec2-instance-contents')),
  CLOUD: lazy(() => import('@thesvg/react/amazon-web-services')),
  AP: lazy(() => import('@thesvg/react/aws-amazon-api-gateway')),
};

const AZURE_ICONS = {
  ROUTER: lazy(() => import('@thesvg/react/azure-virtual-router')),
  SWITCH: lazy(() => import('@thesvg/react/azure-virtual-networks')),
  FIREWALL: lazy(() => import('@thesvg/react/azure-azure-firewall-manager')),
  SERVER: lazy(() => import('@thesvg/react/azure-virtual-machine')),
  CLOUD: lazy(() => import('@thesvg/react/microsoft-azure')),
  AP: lazy(() => import('@thesvg/react/azure-mobile-networks')),
};

const GCP_ICONS = {
  ROUTER: lazy(() => import('@thesvg/react/gcp-cloud-router')),
  SWITCH: lazy(() => import('@thesvg/react/gcp-virtual-private-cloud')),
  FIREWALL: lazy(() => import('@thesvg/react/gcp-cloud-firewall-rules')),
  SERVER: lazy(() => import('@thesvg/react/gcp-compute-engine')),
  CLOUD: lazy(() => import('@thesvg/react/google-cloud')),
  AP: lazy(() => import('@thesvg/react/gcp-cloud-endpoints')),
};

const DEFAULT_ICONS = {
  ROUTER: Router,
  SWITCH: SwitchCamera,
  FIREWALL: ShieldCheck,
  SERVER: Server,
  CLOUD: Cloud,
  AP: Wifi,
  PC: Monitor,
  LAPTOP: Laptop,
  TABLET: Tablet,
  SMARTPHONE: Smartphone,
  PRINTER: Printer,
  CUSTOM: Box,
};

const dynamicIconCache = new Map<string, React.LazyExoticComponent<React.ComponentType<any>>>();

export function getDynamicIcon(iconKey: string) {
  if (!dynamicIconCache.has(iconKey)) {
    dynamicIconCache.set(
      iconKey,
      lazy(() =>
        import(`../node_modules/@thesvg/react/dist/${iconKey}.js`)
          .catch(() => import(`../node_modules/@thesvg/react/dist/${iconKey}.cjs`))
          .catch((err) => {
            const url = `https://thesvg.org/icons/${iconKey}.svg`;
            console.warn("Failed to load icon:", url);
            return {
              default: (props: any) => {
                const FallbackIcon = (props?.brand === 'hosting' || iconKey.includes('host') || iconKey.includes('server')) ? Server : Box;
                return <FallbackIcon size={props?.size || 24} className={props?.className} color={props?.color} />;
              }
            };
          })
      )
    );
  }
  return dynamicIconCache.get(iconKey)!;
}

interface IconErrorBoundaryProps {
  iconKey?: string;
  fallback: React.ReactNode;
  children: React.ReactNode;
}

interface IconErrorBoundaryState {
  hasError: boolean;
}

export class IconErrorBoundary extends React.Component<IconErrorBoundaryProps, IconErrorBoundaryState> {
  constructor(props: IconErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any) {
    const url = `https://thesvg.org/icons/${this.props.iconKey || 'unknown'}.svg`;
    console.warn("Failed to load icon:", url);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface BrandIconProps {
  type: string;
  brand: string;
  iconKey?: string;
  size?: number;
  className?: string;
  color?: string;
  isDark?: boolean;
}

const BRAND_CATEGORIES = new Set([
  'aws', 'azure', 'gcp', 'devtool', 'devtools', 'microsoft', 'hosting'
]);

export function BrandIcon({ type, brand, iconKey, size = 24, className, color, isDark }: BrandIconProps) {
  let isBrand = false;
  let IconComponent: any = DEFAULT_ICONS[type as keyof typeof DEFAULT_ICONS] || Box;

  const normalizedBrand = (brand || '').toLowerCase();
  const isRecognizedBrand = BRAND_CATEGORIES.has(normalizedBrand);

  const targetIconKey = iconKey || (
    type && (
      type.includes('-') ||
      isRecognizedBrand ||
      type.startsWith('aws') ||
      type.startsWith('azure') ||
      type.startsWith('gcp') ||
      type.startsWith('amazon') ||
      type.startsWith('microsoft') ||
      type.startsWith('google') ||
      !(type in DEFAULT_ICONS)
    ) ? type : null
  );

  if (targetIconKey) {
    IconComponent = getDynamicIcon(targetIconKey);
    isBrand = true;
  } else if (brand !== 'default') {
    if (normalizedBrand === 'aws' && AWS_ICONS[type as keyof typeof AWS_ICONS]) {
      IconComponent = AWS_ICONS[type as keyof typeof AWS_ICONS];
      isBrand = true;
    } else if (normalizedBrand === 'azure' && AZURE_ICONS[type as keyof typeof AZURE_ICONS]) {
      IconComponent = AZURE_ICONS[type as keyof typeof AZURE_ICONS];
      isBrand = true;
    } else if (normalizedBrand === 'gcp' && GCP_ICONS[type as keyof typeof GCP_ICONS]) {
      IconComponent = GCP_ICONS[type as keyof typeof GCP_ICONS];
      isBrand = true;
    }
  }

  const Fallback = normalizedBrand === 'hosting' ? Server : (DEFAULT_ICONS[type as keyof typeof DEFAULT_ICONS] || Box);

  const isGCP = normalizedBrand === 'gcp' || (targetIconKey && (targetIconKey.startsWith('gcp') || targetIconKey.startsWith('google')));
  const brandClassName = isGCP ? `${className || ''} gcp-icon`.trim() : (className || '');

  const props = isBrand 
    ? {
        width: "100%",
        height: "100%",
        preserveAspectRatio: "xMidYMid meet",
        className: `w-full h-full max-w-full max-h-full ${brandClassName}`.trim(),
        brand: normalizedBrand,
      } 
    : { size, className, color };

  return (
    <div
      className={`w-8 h-8 flex items-center justify-center overflow-hidden shrink-0 brand-icon-container brand-icon-contrast`}
      style={{ width: size, height: size }}
    >
      <Suspense fallback={<Fallback size={size} className={className} color={color} />}>
        <IconErrorBoundary iconKey={targetIconKey || type} fallback={<Fallback size={size} className={className} color={color} />}>
          <IconComponent {...props} />
        </IconErrorBoundary>
      </Suspense>
    </div>
  );
}

export const BRAND_LABELS: Record<string, Record<string, string>> = {
  aws: {
    ROUTER: "Amazon VPC Router",
    SWITCH: "Amazon VPC ENI",
    FIREWALL: "AWS WAF",
    SERVER: "Amazon EC2",
    CLOUD: "Amazon Web Services",
    AP: "Amazon API Gateway",
  },
  azure: {
    ROUTER: "Virtual Router",
    SWITCH: "Virtual Networks",
    FIREWALL: "Azure Firewall",
    SERVER: "Virtual Machine",
    CLOUD: "Microsoft Azure",
    AP: "Mobile Networks",
  },
  gcp: {
    ROUTER: "Cloud Router",
    SWITCH: "Virtual Private Cloud",
    FIREWALL: "Cloud Firewall Rules",
    SERVER: "Compute Engine",
    CLOUD: "Google Cloud",
    AP: "Cloud Endpoints",
  }
};
